"use client"

import Link from 'next/link'
import { Home, Pause, Play, RotateCcw, Volume2, VolumeX } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { trackEvent } from '@/lib/analytics'
import { unlockLawnSpaceAudio } from '@/lib/lawn-space-shooter/audio'
import {
  readSfxEnabled,
  writeSfxEnabled,
} from '@/lib/lawn-space-shooter/preferences'
import {
  formatLawnSpaceScore,
} from '@/lib/lawn-space-shooter/scoring'
import {
  readLawnSpaceBestScore,
  writeLawnSpaceBestScore,
} from '@/lib/lawn-space-shooter/storage'
import type {
  LawnSpaceDeviceType,
  LawnSpacePhase,
  LawnSpaceRunSummary,
  LawnSpaceStats,
} from '@/lib/lawn-space-shooter/types'
import type {
  LawnSpaceDebugMode,
  LawnSpaceGameHandle,
} from './game/create-game'

const initialStats: LawnSpaceStats = {
  score: 0,
  wave: 1,
  hearts: 3,
  bestScore: 0,
}

function getDeviceType(): LawnSpaceDeviceType {
  if (typeof window === 'undefined') return 'desktop'
  return window.matchMedia('(pointer: coarse)').matches ? 'mobile' : 'desktop'
}

function getDebugMode(): LawnSpaceDebugMode {
  if (typeof window === 'undefined') return null
  if (process.env.NODE_ENV === 'production') return null

  const params = new URLSearchParams(window.location.search)
  const mode = params.get('debugGame')
  if (mode === 'game-over') return 'game-over'
  if (mode === 'perfect-lawn') return 'perfect-lawn'
  return null
}

export function LawnSpaceShooterGame() {
  const parentRef = useRef<HTMLDivElement | null>(null)
  const gameRef = useRef<LawnSpaceGameHandle | null>(null)
  const viewedRef = useRef(false)
  const [phase, setPhase] = useState<LawnSpacePhase>('loading')
  const [stats, setStats] = useState<LawnSpaceStats>(initialStats)
  const [completion, setCompletion] = useState<LawnSpaceRunSummary | null>(null)
  const [sfxEnabled, setSfxEnabled] = useState(() => readSfxEnabled())
  const debugMode = useMemo(() => getDebugMode(), [])

  useEffect(() => {
    if (viewedRef.current) return
    viewedRef.current = true
    trackEvent('lawn_space_game_viewed', {
      device_type: getDeviceType(),
    })
  }, [])

  useEffect(() => {
    const parent = parentRef.current
    if (!parent || gameRef.current) return

    let cancelled = false
    let handle: LawnSpaceGameHandle | null = null
    const bestScore = readLawnSpaceBestScore(window.localStorage)
    setStats((current) => ({ ...current, bestScore }))

    async function mountGame() {
      const { createLawnSpaceGame } = await import('./game/create-game')
      if (cancelled || !parentRef.current) return

      handle = createLawnSpaceGame({
        parent: parentRef.current,
        bestScore,
        debugMode,
        callbacks: {
          onReady: () => setPhase('title'),
          onPhaseChange: setPhase,
          onStatsChange: (nextStats) => {
            setStats((current) => ({
              ...nextStats,
              bestScore: Math.max(current.bestScore, nextStats.bestScore),
            }))
          },
          onComplete: (summary) => {
            const nextBestScore = writeLawnSpaceBestScore(summary.score, window.localStorage)
            setCompletion(summary)
            setStats((current) => ({
              ...current,
              score: summary.score,
              wave: summary.wave,
              hearts: summary.outcome === 'perfect_lawn' ? current.hearts : 0,
              bestScore: nextBestScore,
            }))
            trackEvent('lawn_space_game_completed', {
              score: summary.score,
              wave: summary.wave,
              rank: summary.rank,
              duration_seconds: summary.durationSeconds,
              outcome: summary.outcome,
              failure_reason: summary.failureReason,
              powerups_collected: summary.powerupsCollected,
              enemies_destroyed: summary.enemiesDestroyed,
              cta_target: summary.cta.target,
              device_type: getDeviceType(),
            })
          },
        },
      })
      gameRef.current = handle
    }

    mountGame()

    return () => {
      cancelled = true
      handle?.destroy()
      if (gameRef.current === handle) gameRef.current = null
      parent.replaceChildren()
    }
  }, [debugMode])

  const startRun = useCallback(() => {
    setCompletion(null)
    unlockLawnSpaceAudio()
    trackEvent('lawn_space_game_started', {
      device_type: getDeviceType(),
      best_score: stats.bestScore,
    })
    gameRef.current?.start()
  }, [stats.bestScore])

  const restartRun = useCallback(() => {
    setCompletion(null)
    trackEvent('lawn_space_game_restarted', {
      score: stats.score,
      wave: stats.wave,
      device_type: getDeviceType(),
    })
    gameRef.current?.restart()
  }, [stats.score, stats.wave])

  const togglePause = useCallback(() => {
    gameRef.current?.togglePause()
  }, [])

  const resumeRun = useCallback(() => {
    gameRef.current?.resume()
  }, [])

  const toggleSfx = useCallback(() => {
    setSfxEnabled((current) => {
      const next = !current
      writeSfxEnabled(next)
      trackEvent('lawn_space_game_sfx_toggled', {
        enabled: next,
        device_type: getDeviceType(),
      })
      return next
    })
  }, [])

  const trackCtaClick = useCallback(() => {
    if (!completion) return
    trackEvent('lawn_space_game_cta_clicked', {
      score: completion.score,
      wave: completion.wave,
      rank: completion.rank,
      duration_seconds: completion.durationSeconds,
      outcome: completion.outcome,
      failure_reason: completion.failureReason,
      cta_target: completion.cta.target,
      device_type: getDeviceType(),
    })
  }, [completion])

  const isEnded = phase === 'game-over' || phase === 'perfect-lawn'
  const canPause = phase === 'playing' || phase === 'paused' || phase === 'wave-clear'

  return (
    <section className="min-h-[100svh] overflow-hidden bg-[#05110d] text-[#f8f4d8]">
      <div className="mx-auto flex min-h-[100svh] w-full max-w-7xl flex-col gap-3 px-3 py-3 sm:px-5 sm:py-4">
        <div className="flex min-h-12 items-center justify-between gap-3">
          <Link
            href="/"
            className="inline-flex size-10 items-center justify-center rounded-md border border-[#315f4b] bg-[#0b1d16] text-[#f8f4d8] transition hover:border-[#8fff7f] hover:text-[#8fff7f] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8fff7f]"
            aria-label="Back to What Grass Is This home"
            title="Back home"
          >
            <Home className="size-5" aria-hidden="true" />
          </Link>

          <div
            className="grid flex-1 grid-cols-2 gap-2 text-center font-mono text-[0.72rem] uppercase tracking-[0.08em] text-[#c9dcc9] sm:flex sm:items-center sm:justify-center sm:text-xs"
            aria-live="polite"
          >
            <span data-testid="lawn-space-score">Score {formatLawnSpaceScore(stats.score)}</span>
            <span>Wave {Math.min(stats.wave, 6)}/6</span>
            <span>HP {stats.hearts}</span>
            <span>Best {formatLawnSpaceScore(stats.bestScore)}</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={toggleSfx}
              className="inline-flex size-10 items-center justify-center rounded-md border border-[#315f4b] bg-[#0b1d16] text-[#f8f4d8] transition hover:border-[#8fff7f] hover:text-[#8fff7f] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8fff7f]"
              aria-label={sfxEnabled ? 'Mute sound' : 'Unmute sound'}
              aria-pressed={!sfxEnabled}
              title={sfxEnabled ? 'Mute' : 'Unmute'}
              data-testid="lawn-space-sound-toggle"
            >
              {sfxEnabled ? (
                <Volume2 className="size-5" aria-hidden="true" />
              ) : (
                <VolumeX className="size-5" aria-hidden="true" />
              )}
            </button>

            <button
              type="button"
              onClick={togglePause}
              disabled={!canPause}
              className="inline-flex size-10 items-center justify-center rounded-md border border-[#315f4b] bg-[#0b1d16] text-[#f8f4d8] transition hover:border-[#8fff7f] hover:text-[#8fff7f] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8fff7f] disabled:cursor-not-allowed disabled:opacity-40"
              aria-label={phase === 'paused' ? 'Resume game' : 'Pause game'}
              title={phase === 'paused' ? 'Resume' : 'Pause'}
            >
              {phase === 'paused' ? (
                <Play className="size-5" aria-hidden="true" />
              ) : (
                <Pause className="size-5" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>

        <div
          data-testid="lawn-space-game-shell"
          className="relative min-h-[520px] flex-1 overflow-hidden rounded-md border border-[#315f4b] bg-[#06120e] shadow-[0_0_0_1px_rgba(143,255,127,0.18),0_22px_70px_rgba(0,0,0,0.5)] touch-none"
        >
          <div
            ref={parentRef}
            data-testid="lawn-space-canvas-parent"
            className="absolute inset-0 touch-none [image-rendering:pixelated] [&_canvas]:!h-full [&_canvas]:!w-full [&_canvas]:touch-none [&_canvas]:[image-rendering:pixelated]"
          />

          {(phase === 'loading' || phase === 'title') && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#06120e]/76 px-5 text-center backdrop-blur-[2px]">
              <div className="max-w-xl">
                <p className="mb-3 font-mono text-xs uppercase tracking-[0.24em] text-[#79d7ff]">
                  What Grass Is This arcade
                </p>
                <h1 className="font-mono text-4xl font-black uppercase leading-none text-[#ffe56e] sm:text-6xl">
                  Lawn Force One
                </h1>
                <p className="mx-auto mt-4 max-w-md text-sm leading-6 text-[#d9ead7] sm:text-base">
                  Blast crabgrass scouts, dodge dry-soil meteors, and turn a clean run into a real lawn-care next step.
                </p>
                <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={startRun}
                    disabled={phase === 'loading'}
                    className="inline-flex min-h-12 items-center gap-2 rounded-md bg-[#8fff7f] px-5 py-3 font-mono text-sm font-black uppercase tracking-[0.08em] text-[#06120e] transition hover:bg-[#ffe56e] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#ffe56e] disabled:cursor-wait disabled:opacity-60"
                  >
                    <Play className="size-5" aria-hidden="true" />
                    Start Run
                  </button>
                  <span className="font-mono text-xs uppercase tracking-[0.12em] text-[#aac7ad]">
                    WASD, arrows, or drag
                  </span>
                </div>
              </div>
            </div>
          )}

          {phase === 'paused' && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#06120e]/78 px-5 text-center backdrop-blur-[2px]">
              <div>
                <h2 className="font-mono text-3xl font-black uppercase text-[#ffe56e]">Paused</h2>
                <div className="mt-5 flex flex-wrap justify-center gap-3">
                  <button
                    type="button"
                    onClick={resumeRun}
                    className="inline-flex min-h-11 items-center gap-2 rounded-md bg-[#8fff7f] px-4 py-2 font-mono text-sm font-black uppercase text-[#06120e] transition hover:bg-[#ffe56e] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#ffe56e]"
                  >
                    <Play className="size-4" aria-hidden="true" />
                    Resume
                  </button>
                  <button
                    type="button"
                    onClick={restartRun}
                    className="inline-flex min-h-11 items-center gap-2 rounded-md border border-[#315f4b] bg-[#0b1d16] px-4 py-2 font-mono text-sm font-black uppercase text-[#f8f4d8] transition hover:border-[#8fff7f] hover:text-[#8fff7f] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8fff7f]"
                  >
                    <RotateCcw className="size-4" aria-hidden="true" />
                    Restart
                  </button>
                </div>
              </div>
            </div>
          )}

          {isEnded && completion && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#06120e]/82 px-5 text-center backdrop-blur-[2px]">
              <div className="max-w-xl">
                <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#79d7ff]">
                  {completion.outcome === 'perfect_lawn' ? 'Perfect lawn clear' : 'Run complete'}
                </p>
                <h2 className="mt-3 font-mono text-3xl font-black uppercase text-[#ffe56e] sm:text-5xl">
                  {completion.rank}
                </h2>
                <div className="mt-4 grid grid-cols-3 gap-2 font-mono text-xs uppercase tracking-[0.08em] text-[#d9ead7] sm:text-sm">
                  <span>Score {formatLawnSpaceScore(completion.score)}</span>
                  <span>Wave {completion.wave}</span>
                  <span>Best {formatLawnSpaceScore(stats.bestScore)}</span>
                </div>
                <p className="mx-auto mt-5 max-w-md text-sm leading-6 text-[#d9ead7] sm:text-base">
                  {completion.cta.description}
                </p>
                <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
                  <Link
                    href={completion.cta.href}
                    onClick={trackCtaClick}
                    className="inline-flex min-h-12 items-center justify-center rounded-md bg-[#8fff7f] px-5 py-3 font-mono text-sm font-black uppercase tracking-[0.08em] text-[#06120e] transition hover:bg-[#ffe56e] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#ffe56e]"
                  >
                    {completion.cta.label}
                  </Link>
                  <button
                    type="button"
                    onClick={restartRun}
                    className="inline-flex min-h-12 items-center gap-2 rounded-md border border-[#315f4b] bg-[#0b1d16] px-5 py-3 font-mono text-sm font-black uppercase tracking-[0.08em] text-[#f8f4d8] transition hover:border-[#8fff7f] hover:text-[#8fff7f] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8fff7f]"
                  >
                    <RotateCcw className="size-5" aria-hidden="true" />
                    Play Again
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex min-h-6 items-center justify-between gap-3 font-mono text-[0.68rem] uppercase tracking-[0.08em] text-[#8ca99a]">
          <span data-testid="lawn-space-phase">{phase}</span>
          <span>{stats.activePowerup ?? 'Auto-fire online'}</span>
        </div>
      </div>
    </section>
  )
}
